import MailIcon from '@material-design-icons/svg/filled/mail.svg?react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useAuth, AuthStateType } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { isLeftResult } from '../../lib/trpcTypeGuards';
import { unknownToLeft } from '../../lib/unknownToLeft';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  getPublisherInviteStateClasses,
  getPublisherInviteStateLabel,
} from '../../utils/publisherInvites';
import { Button } from '../Button';
import { LayoutWithBgGradient } from '../LayoutWithBgGradient';
import { PageHeader } from '../PageHeader';

type PublisherInviteLanding = RightResult<
  RouterOutputs['getPublisherInviteLanding']
>;

export function PublisherInvitePage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [auth, { signOut, refreshUser, setOnboardingState }] = useAuth();
  const { refresh: refreshPublisherContext } = usePublisher();
  const {
    data: landing,
    setData: setLanding,
    isLoading: loading,
    setIsLoading,
    run,
  } = useAsyncData<PublisherInviteLanding | null>({
    initialData: null,
    initialLoading: true,
  });
  const [submittingAction, setSubmittingAction] = useState<
    'accept' | 'decline' | null
  >(null);
  const token = searchParams.get('token')?.trim() || '';
  const returnToInvite = `${location.pathname}${location.search}${location.hash}`;

  usePageTitle('Publisher Invite | Publishing Portal');

  async function loadLanding() {
    if (!token) {
      setLanding({ state: 'Invalid', invite: null });
      setIsLoading(false);
      return;
    }

    await run(
      () =>
        trpc.getPublisherInviteLanding.query({ token }).catch(unknownToLeft),
      {
        onSuccess: (result, helpers) => {
          if (isLeftResult(result)) {
            toast.error('Failed to load invite', {
              description: result.left.message,
              duration: 5000,
            });
            captureAndReportError(result.left);
            helpers.setData({ state: 'Invalid', invite: null });
            return;
          }

          helpers.setData(result.right);
        },
      },
    );
  }

  useEffect(() => {
    void loadLanding();
  }, [token]);

  async function handleAccept() {
    if (!landing?.invite) {
      return;
    }

    setSubmittingAction('accept');

    const result = await trpc.acceptPublisherInvite
      .mutate({ inviteId: landing.invite.id })
      .catch(unknownToLeft);

    setSubmittingAction(null);

    if (isLeftResult(result)) {
      toast.error('Failed to accept invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    setOnboardingState('Complete');
    await refreshUser();
    await refreshPublisherContext();

    toast.success('Publisher access granted', {
      description:
        result.right.status === 'already_member'
          ? 'This publisher is already available in your workspace.'
          : 'The invitation was accepted successfully.',
      duration: 4000,
    });

    if (result.right.becameFirstPublisherAccess) {
      navigate('/dashboard', { replace: true });
      return;
    }

    await loadLanding();
  }

  async function handleDecline() {
    if (!landing?.invite) {
      return;
    }

    setSubmittingAction('decline');

    const result = await trpc.declinePublisherInvite
      .mutate({ inviteId: landing.invite.id })
      .catch(unknownToLeft);

    setSubmittingAction(null);

    if (isLeftResult(result)) {
      toast.error('Failed to decline invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    toast.success('Invite declined');
    await loadLanding();
  }

  function navigateToAuth(formType: 'signin' | 'signup') {
    navigate('/auth', {
      state: {
        from: returnToInvite,
        authFormType: formType,
      },
    });
  }

  async function handleSwitchAccount() {
    await signOut();
    navigateToAuth('signin');
  }

  function renderPendingInviteActions() {
    if (!landing?.invite) {
      return null;
    }

    switch (auth.type) {
      case AuthStateType.Verifying:
        return (
          <div className="text-sm text-neutral-400">Checking account…</div>
        );
      case AuthStateType.NoToken:
        return (
          <div className="flex flex-wrap gap-3">
            <Button type="button" onClick={() => navigateToAuth('signin')}>
              Sign In
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigateToAuth('signup')}
            >
              Create Account
            </Button>
          </div>
        );
      case AuthStateType.Authenticated:
        if (
          auth.user.email.toLowerCase() !==
          landing.invite.invitedEmail.toLowerCase()
        ) {
          return (
            <div className="space-y-4">
              <div className="text-sm leading-6 text-neutral-400">
                You are signed in as{' '}
                <span className="text-white">{auth.user.email}</span>. This
                invite was sent to{' '}
                <span className="text-white">
                  {landing.invite.invitedEmail}
                </span>
                . Switch to the invited email to accept it.
              </div>
              <div className="flex flex-wrap gap-3">
                <Button type="button" onClick={() => void signOut()}>
                  Sign Out
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void handleSwitchAccount()}
                >
                  Use Different Email
                </Button>
              </div>
            </div>
          );
        }

        if (!auth.user.emailVerified) {
          return (
            <div className="space-y-4">
              <div className="text-sm leading-6 text-neutral-400">
                Verify <span className="text-white">{auth.user.email}</span> to
                accept this invite. If you lose the original invite link during
                verification, you can finish from the publisher access page.
              </div>
              <Button
                type="button"
                onClick={() =>
                  navigate('/verify-email', {
                    state: {
                      from: returnToInvite,
                    },
                  })
                }
              >
                Verify Your Email
              </Button>
            </div>
          );
        }

        return (
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              submitting={submittingAction === 'accept'}
              disabled={submittingAction !== null}
              onClick={() => void handleAccept()}
            >
              Accept Invite
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={submittingAction !== null}
              onClick={() => void handleDecline()}
            >
              Decline
            </Button>
          </div>
        );
    }
  }

  function renderStateBody() {
    if (!landing) {
      return null;
    }

    switch (landing.state) {
      case 'Invalid':
        return (
          <div className="space-y-4">
            <p className="text-sm leading-6 text-neutral-400">
              This invite link is invalid or no longer available.
            </p>
            <Button type="button" onClick={() => navigate('/auth')}>
              Go to Sign In
            </Button>
          </div>
        );
      case 'BlockedDeletedAccount':
        return (
          <p className="text-sm leading-6 text-neutral-400">
            This invite is blocked because the invited email belongs to a
            previously removed account. Contact support to restore that account
            before accepting publisher access.
          </p>
        );
      case 'Accepted':
        return (
          <div className="space-y-4">
            <p className="text-sm leading-6 text-neutral-400">
              This invite has already been accepted.
            </p>
            <Button type="button" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        );
      case 'Declined':
        return (
          <p className="text-sm leading-6 text-neutral-400">
            This invite was declined. Ask a team manager to send a new invite if
            you still need publisher access.
          </p>
        );
      case 'Revoked':
        return (
          <p className="text-sm leading-6 text-neutral-400">
            This invite has been revoked by the publisher.
          </p>
        );
      case 'Expired':
        return (
          <p className="text-sm leading-6 text-neutral-400">
            This invite has expired. Ask a team manager to send a fresh invite.
          </p>
        );
      case 'Pending':
        return renderPendingInviteActions();
    }
  }

  return (
    <LayoutWithBgGradient>
      <div className="min-h-screen w-screen overflow-y-auto">
        <PageHeader />
        <div className="mx-auto flex max-w-3xl flex-col gap-8 px-8 py-16">
          <div className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-[0.24em] text-neutral-400">
              Publisher Invite
            </div>
            <h1 className="text-4xl font-semibold text-white">
              Review your publisher invitation.
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-neutral-400">
              Sign in with the invited email address, verify it if needed, and
              accept the invite to join the publisher.
            </p>
          </div>

          <div className="rounded-2xl border border-neutral-800 bg-black/30 p-6">
            {loading ? (
              <div className="text-sm text-neutral-400">Loading invite…</div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <MailIcon className="size-5 fill-white" />
                    <div className="text-lg font-semibold text-white">
                      {landing?.invite?.publisherName || 'Publisher invite'}
                    </div>
                  </div>

                  {landing && (
                    <div
                      className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${getPublisherInviteStateClasses(landing.state)}`}
                    >
                      {getPublisherInviteStateLabel(landing.state)}
                    </div>
                  )}
                </div>

                {landing?.invite && (
                  <div className="grid gap-4 rounded-xl border border-neutral-800 bg-neutral-950/60 p-4 text-sm text-neutral-400 md:grid-cols-2">
                    <div>
                      <div className="mb-1 text-xs uppercase tracking-[0.18em] text-neutral-500">
                        Publisher
                      </div>
                      <div className="text-white">
                        {landing.invite.publisherName}
                      </div>
                      <div>{landing.invite.publisherType}</div>
                    </div>

                    <div>
                      <div className="mb-1 text-xs uppercase tracking-[0.18em] text-neutral-500">
                        Role
                      </div>
                      <div className="text-white">{landing.invite.role}</div>
                    </div>

                    <div>
                      <div className="mb-1 text-xs uppercase tracking-[0.18em] text-neutral-500">
                        Invited Email
                      </div>
                      <div className="text-white">
                        {landing.invite.invitedEmail}
                      </div>
                    </div>

                    <div>
                      <div className="mb-1 text-xs uppercase tracking-[0.18em] text-neutral-500">
                        Expires
                      </div>
                      <div className="text-white">
                        {new Date(landing.invite.expiresAt).toLocaleString()}
                      </div>
                    </div>

                    {(landing.invite.invitedByName ||
                      landing.invite.invitedByEmail) && (
                      <div className="md:col-span-2">
                        <div className="mb-1 text-xs uppercase tracking-[0.18em] text-neutral-500">
                          Invited By
                        </div>
                        <div className="text-white">
                          {landing.invite.invitedByName ||
                            landing.invite.invitedByEmail}
                          {landing.invite.invitedByName &&
                            landing.invite.invitedByEmail &&
                            ` • ${landing.invite.invitedByEmail}`}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div>{renderStateBody()}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutWithBgGradient>
  );
}
